import React, { useCallback, useEffect, useState } from "react";
import "./admin.css";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtScore = (n) => Math.round(n || 0).toLocaleString("en-US");
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

export default function ScoresAdmin() {
  const [scores, setScores] = useState([]);
  const [mode, setMode] = useState("standard"); // standard | easy | all
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({ name: "", score: "" });

  const fetchScores = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/highscores?limit=50&mode=${mode}`, { credentials: "include" });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setScores(data.scores || []);
    } catch (e) {
      console.warn("failed to fetch scores", e);
      setError("שגיאה בטעינת השיאים");
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    fetchScores();
  }, [fetchScores]);

  const startEdit = (s) => {
    setEditId(s.id);
    setDraft({ name: s.name, score: String(Math.round(s.score)) });
  };

  const cancelEdit = () => {
    setEditId(null);
    setDraft({ name: "", score: "" });
  };

  const saveEdit = async (id) => {
    const name = draft.name.trim().slice(0, 16);
    const score = parseFloat(draft.score);
    if (!name || isNaN(score) || score < 0) {
      setError("שם או ציון לא תקין");
      return;
    }
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/highscores/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, score }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      cancelEdit();
      fetchScores();
    } catch (e) {
      console.warn("failed to save", e);
      setError("שמירה נכשלה");
    }
  };

  const removeScore = async (id, name) => {
    if (!window.confirm(`למחוק את השיא של "${name}"? פעולה זו אינה הפיכה.`)) return;
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/highscores/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      fetchScores();
    } catch (e) {
      console.warn("failed to delete", e);
      setError("מחיקה נכשלה");
    }
  };

  return (
    <div className="scores-admin" data-testid="scores-admin">
      <div className="admin-toolbar">
        <div className="admin-sub">ניהול טבלת שיאים</div>
        <button
          className="rbtn small"
          data-testid="scores-refresh-btn"
          onClick={fetchScores}
          disabled={loading}
        >
          {loading ? "...טוען" : "רענון ⟳"}
        </button>
      </div>
      <div className="scores-mode-filter" data-testid="scores-mode-filter">
        {[
          { id: "standard", label: "משחק מקורי" },
          { id: "easy", label: "משחק עם הקלות" },
          { id: "all", label: "הכל" },
        ].map((m) => (
          <button
            key={m.id}
            className={"mode-pill" + (mode === m.id ? " active" : "")}
            data-testid={`scores-mode-${m.id}`}
            onClick={() => setMode(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>
      {error && <div className="admin-error" data-testid="scores-error">{error}</div>}
      {!loading && scores.length === 0 ? (
        <div className="admin-empty" data-testid="scores-empty">
          אין עדיין שיאים רשומים
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="scores-table">
            <thead>
              <tr>
                <th>#</th>
                <th>שם</th>
                <th>ציון</th>
                <th>מצב</th>
                <th>תאריך</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {scores.map((s, i) => (
                <tr key={s.id} data-testid={`score-row-${i + 1}`}>
                  <td className="cell-num cell-rank">{i + 1}</td>
                  <td className="cell-name">
                    {editId === s.id ? (
                      <input
                        className="score-edit-input"
                        data-testid="edit-name-input"
                        type="text"
                        maxLength={16}
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      />
                    ) : (
                      s.name
                    )}
                  </td>
                  <td className="cell-num">
                    {editId === s.id ? (
                      <input
                        className="score-edit-input"
                        data-testid="edit-score-input"
                        type="number"
                        min="0"
                        value={draft.score}
                        onChange={(e) => setDraft({ ...draft, score: e.target.value })}
                      />
                    ) : (
                      fmtScore(s.score)
                    )}
                  </td>
                  <td>
                    <span
                      className={"score-badge " + (s.standard ? "std" : "easy")}
                      data-testid={`score-mode-${i + 1}`}
                    >
                      {s.standard ? "מקורי" : "הקלות"}
                    </span>
                  </td>
                  <td className="cell-dev">{fmtDate(s.created_at)}</td>
                  <td className="cell-actions">
                    {editId === s.id ? (
                      <>
                        <button
                          className="rbtn tiny primary"
                          data-testid="edit-save-btn"
                          onClick={() => saveEdit(s.id)}
                        >
                          שמור
                        </button>
                        <button
                          className="rbtn tiny"
                          data-testid="edit-cancel-btn"
                          onClick={cancelEdit}
                        >
                          ביטול
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="rbtn tiny"
                          data-testid={`edit-btn-${i + 1}`}
                          onClick={() => startEdit(s)}
                        >
                          ערוך
                        </button>
                        <button
                          className="rbtn tiny danger"
                          data-testid={`delete-btn-${i + 1}`}
                          onClick={() => removeScore(s.id, s.name)}
                        >
                          מחק
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
